import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Avatar,
  Chip,
  Button,
  Divider,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Box,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  VerifiedUser as VerifiedUserIcon,
  Security as SecurityIcon,
  ShoppingBag as ShoppingBagIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import ClientService from '../service/client.service';

export default function ClientProfilePage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const getAuthToken = () => {
    const cookieMatch = document.cookie.match(/(?:^|; )buyer_token=([^;]*)/);
    if (cookieMatch) {
      return decodeURIComponent(cookieMatch[1]);
    }
    return localStorage.getItem('buyer_token') || sessionStorage.getItem('buyer_token');
  };

  useEffect(() => {
    let rawToken = getAuthToken();
    if (rawToken === 'true' || rawToken === 'undefined' || rawToken === 'null') {
      localStorage.removeItem('buyer_token');
      sessionStorage.removeItem('buyer_token');
      rawToken = null;
    }

    const validToken = rawToken;
    if (!validToken) {
      setLoading(false);
      navigate('/login', { replace: true });
      return;
    }
    let isSubscribed = true;
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const cleanToken = validToken.replace(/^"|"$/g, '');
        const res = await ClientService.getProfile(cleanToken);
        if (res?.data && isSubscribed) {
          setProfile(res.data.data || res.data);
        }
      } catch (err) {
        if (isSubscribed) {
          console.error('Failed to fetch client profile:', err);
          toast.error('Session expired or invalid. Please log in again.');
          navigate('/login', { replace: true });
        }
      } finally {
        if (isSubscribed) setLoading(false);
      }
    };

    fetchProfile();
    return () => {
      isSubscribed = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 5, flex: 1 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
          borderRadius: 4,
          background: 'linear-gradient(135deg, #5F4B3B 0%, #3E3126 100%)',
          color: '#fff',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          boxShadow: '0 20px 40px rgba(62, 49, 38, 0.15)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
          <Avatar
            sx={{
              width: 72,
              height: 72,
              bgcolor: 'primary.main',
              fontSize: '2rem',
              fontWeight: 700,
              border: '3px solid rgba(255,255,255,0.2)',
            }}
          >
            {profile?.firstName ? profile?.firstName[0]?.toUpperCase() : 'C'}
          </Avatar>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff' }}>
                {loading ? <Skeleton width={180} /> : `${profile?.firstName || 'Client'} ${profile?.lastName || ''}`}
              </Typography>
              <Chip
                icon={<VerifiedUserIcon style={{ color: '#10b981', fontSize: 16 }} />}
                label="Verified Buyer / Client"
                size="small"
                sx={{ bgcolor: 'rgba(16, 185, 129, 0.15)', color: '#10b981', fontWeight: 600, border: '1px solid rgba(16, 185, 129, 0.3)' }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
              client.sourceseas.com — Subdomain Dedicated Client Portal
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #D6CEC4', bgcolor: '#fff' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}>
              Client Details
            </Typography>

            {loading ? (
              <Stack spacing={2}>
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
              </Stack>
            ) : (
              <Grid container spacing={2.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <PersonIcon sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Full Name</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>{profile?.firstName} {profile?.lastName}</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <EmailIcon sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Email Address</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>{profile?.email || 'N/A'}</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <PhoneIcon sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Phone Number</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>{profile?.phone || 'N/A'}</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <BusinessIcon sx={{ color: 'primary.main' }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Company Name</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>{profile?.company_name || 'N/A'}</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Divider sx={{ my: 1, borderColor: '#D6CEC4' }} />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    <LocationOnIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">Location</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {[profile?.address, profile?.city, profile?.state, profile?.country].filter(Boolean).join(', ') || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Card elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #D6CEC4', bgcolor: '#fff', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Chip label="CLIENT SUBDOMAIN" size="small" sx={{ fontWeight: 700, bgcolor: 'primary.light', color: 'primary.dark' }} />
                <SecurityIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </Box>

              <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mb: 1 }}>
                Client Dashboard
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                client.sourceseas.com
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                Welcome to the official Client Subdomain Portal! Manage enquiries, sample requests, and orders.
              </Typography>

              <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2.5, border: '1px solid #D6CEC4', mb: 2 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                  <ShoppingBagIcon sx={{ color: 'primary.main' }} fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Verified Client Account
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Active session initialized from Sourceseas core ecosystem.
                </Typography>
              </Box>
            </CardContent>

            <Button
              variant="contained"
              fullWidth
              size="large"
              href={process.env.REACT_APP_REDIRECT_URL}
              sx={{ py: 1.5, borderRadius: 3, textTransform: 'none', fontWeight: 700, fontSize: '15px', bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.dark' } }}
            >
              Return to Sourceseas Main Portal
            </Button>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
